import React, { useMemo } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  HOLIDAY_COLOR,
  VACATION_TEAL,
  CARRY_COLOR,
  TYPE_FILL,
  buildMonthWeeks,
  buildYearDayMarks,
  todayDateStr,
} from "./yearAbsenceCalendarUtils";

const DAY_SIZE = { xs: 26, sm: 28, md: 30 };

function DayCell({ cell, mark, isToday, theme }) {
  if (!cell) {
    return <Box sx={{ width: DAY_SIZE, height: DAY_SIZE }} />;
  }

  const filled = Boolean(mark) && !mark.outline;
  const pending = Boolean(mark?.pending);
  const striped = Boolean(mark?.striped);
  const outline = Boolean(mark?.outline);
  const color = mark?.color;
  const tooltip = mark
    ? `${cell.day} · ${mark.label}${pending ? " (pendiente)" : ""}`
    : isToday
      ? `${cell.day} · Hoy`
      : "";

  const circle = (
    <Box
      sx={{
        width: DAY_SIZE,
        height: DAY_SIZE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        fontSize: { xs: "0.68rem", sm: "0.72rem" },
        fontWeight: filled || outline || isToday ? 800 : 500,
        lineHeight: 1,
        color:
          filled && !striped
            ? "#fff"
            : outline
              ? color
              : filled
                ? color
                : theme.palette.text.primary,
        bgcolor: filled && !striped ? color : "transparent",
        backgroundImage:
          filled && striped
            ? `repeating-linear-gradient(135deg, ${color} 0 3px, #fff 3px 6px)`
            : "none",
        border: outline ? `2px solid ${color}` : "none",
        outline:
          isToday && !outline
            ? `2px solid ${theme.palette.primary.main}`
            : "none",
        outlineOffset: filled ? 1 : 0,
      }}
    >
      {cell.day}
    </Box>
  );

  if (!tooltip) return circle;
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {circle}
    </Tooltip>
  );
}

function MonthGrid({ year, month, marks, today, theme }) {
  const weeks = useMemo(() => buildMonthWeeks(year, month), [year, month]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: "0.78rem", sm: "0.82rem" },
          mb: 0.75,
          color: theme.palette.text.primary,
        }}
      >
        {MONTH_NAMES[month - 1]}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          columnGap: 0.15,
          rowGap: 0.2,
        }}
      >
        {WEEKDAY_SHORT.map((label) => (
          <Typography
            key={label}
            align="center"
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              color: theme.palette.text.secondary,
              lineHeight: 1.4,
            }}
          >
            {label}
          </Typography>
        ))}
        {weeks.flat().map((cell, idx) => (
          <Box key={idx} sx={{ display: "flex", justifyContent: "center" }}>
            <DayCell
              cell={cell}
              mark={cell ? marks[cell.dateStr] : null}
              isToday={Boolean(cell && cell.dateStr === today)}
              theme={theme}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const YearAbsenceCalendar = ({ year, entries = [], onYearChange }) => {
  const theme = useTheme();
  const marks = useMemo(
    () => buildYearDayMarks(entries, year),
    [entries, year],
  );
  const today = todayDateStr();

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 1.25, sm: 2 },
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.65),
        bgcolor: alpha(theme.palette.background.paper, 0.72),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <IconButton
            size="small"
            aria-label="Año anterior"
            onClick={() => onYearChange?.(year - 1)}
            disabled={!onYearChange}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.05rem",
              minWidth: 52,
              textAlign: "center",
            }}
          >
            {year}
          </Typography>
          <IconButton
            size="small"
            aria-label="Año siguiente"
            onClick={() => onYearChange?.(year + 1)}
            disabled={!onYearChange}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.78rem",
            color: theme.palette.text.secondary,
          }}
        >
          Mis ausencias
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1.5 }}
      >
        {[
          {
            color: VACATION_TEAL,
            label: "Vacaciones",
            striped: false,
            outline: false,
          },
          {
            color: CARRY_COLOR,
            label: "Año anterior",
            striped: false,
            outline: false,
          },
          {
            color: VACATION_TEAL,
            label: "Pendiente",
            striped: false,
            outline: true,
          },
          {
            color: HOLIDAY_COLOR,
            label: "Festivo",
            striped: false,
            outline: false,
          },
          {
            color: TYPE_FILL.remote_day,
            label: "Teletrabajo",
            striped: true,
            outline: false,
          },
        ].map((item) => (
          <Stack
            key={item.label}
            direction="row"
            spacing={0.6}
            alignItems="center"
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor:
                  item.striped || item.outline ? "transparent" : item.color,
                backgroundImage: item.striped
                  ? `repeating-linear-gradient(135deg, ${item.color} 0 2px, #fff 2px 4px)`
                  : "none",
                border:
                  item.striped || item.outline
                    ? `1.5px solid ${item.color}`
                    : "none",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(6, minmax(0, 1fr))",
          },
          columnGap: { xs: 1.25, md: 1.5 },
          rowGap: { xs: 2, md: 2.5 },
        }}
      >
        {MONTH_NAMES.map((_, idx) => (
          <MonthGrid
            key={idx}
            year={year}
            month={idx + 1}
            marks={marks}
            today={today}
            theme={theme}
          />
        ))}
      </Box>
    </Box>
  );
};

export default YearAbsenceCalendar;
